import { ISeoPage, ITypeBreadcrumb } from "../../types/types";
import {
  IArchivedPostResponse,
  IDynamicAttributesObject,
  IFindPageQueryString,
  IMetaData,
  IPostData,
  IPostsApiResponse,
  IPostsByTagsApiResponse,
} from "models/post";

import { BaseSeoAdapter } from "./BaseSeoAdapter";
import EssentialData from "private/classes/EssentialData.class";
import {
  ICategoryAsset,
  ICategoryLiteAsset,
} from "models/Assets/ICategoryAsset";
import { ICountryAsset } from "models/Assets/ICountryAsset";
import {isCategoryRealEstate, isShobiddak} from "helpers/osHelpers";
import { labelHelper, trField, urlHelper } from "helpers/themeHelpers";
import { Localization } from "../Localization.class";
import { EnumScreenNames } from "../../../../enums/EnumScreenNames";
import { ICityAsset } from "models/Assets/ICityAsset";
import { slugifyHelper } from "../../helpers/textHelpers";

import {
  generateBreadcrumbs,
  generateBreadcrumbsSchemaOrg,
  generateHrefLangs,
  getCustomIndexStrategy,
} from "libs/seo-widget/helpers/initSeoData";
import { IBankDetails } from "models/loanCalculator";
import { IJobPosterPostView, IJobSeekerPostView } from "models/jobPostView";
import { IFaqCategory, ITitlePlaceholders } from "models/helpCenter";
import {
  IMembershipInfoResponse,
} from "../../../../screens/AccountMembership/_private/types/midView";
import {IMainCategories} from "models/brands";
import {iBasicInfo, iBasicInfoItem, iPostListingData} from "../../../../screens/PostView/_private/Api/types";

export class SingleNodeAdapter extends BaseSeoAdapter {
  private readonly lang2: string;
  private category: ICategoryAsset | ICategoryLiteAsset;
  private subCategory: ICategoryAsset | ICategoryLiteAsset;
  private postDetails: iPostListingData;
  private jobDetails: IJobPosterPostView | IJobSeekerPostView;
  private localization: Localization;
  private dynamicAttributes: {
    [key: string]: iBasicInfoItem | never;
  };
  private jobsDynamicAttributes:
      { [p: string]: IDynamicAttributesObject | never };
  constructor(
    public essentialData: EssentialData,
    private lang: string,
    private country: ICountryAsset
  ) {
    super(essentialData);
    this.lang2 = this.lang === "ar" ? "en" : "ar";
  }

  setLocalization(localization: Localization): void {
    this.localization = localization;
  }

  private getDynamicAttribute(field_name: string) {
    if (this.dynamicAttributes) return this.dynamicAttributes[field_name];

    const dynamicAttributes: { [key: string]: iBasicInfoItem } = {};
    for (let item of this.postDetails.listing.basic_info.items) {
      dynamicAttributes[item.field_label] = item;
    }
    this.dynamicAttributes = dynamicAttributes;
    return this.dynamicAttributes[field_name];
  }

  private getSchemaOrg() {
    const city = this.essentialData.getCityById(this.postDetails.listing.city.id);

    const schemaOrg = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 4,
          name: this.postDetails?.listing.title,
          item: `https://__country_abb__.opensooq.com/${this.lang}/${
            this.postDetails?.listing.post_url.split(`${this.lang}/`)[1]
          }`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: this.subCategory?.reporting_name,
          item:
            "https" +
            "://" +
            "__country_abb__" +
            "." +
            "opensooq.com" +
            "/" +
            urlHelper(this.subCategory, this.lang),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: this.category?.reporting_name,
          item:
            "https://" +
            "__country_abb__" +
            "." +
            "opensooq.com" +
            "/" +
            urlHelper(this.category, this.lang),
        },
        {
          "@type": "ListItem",
          position: 1,
          name: city.name,
          item:
            "https://" +
            "__country_abb__" +
            ".opensooq.com" +
            "/" +
            urlHelper(city, this.lang),
        },
      ],
    });

    const title = this.postDetails?.listing?.title;
    const description = this.postDetails?.listing.masked_description.slice(0, 300);
    const labelField = this.lang == "ar" ? "label" : "label_en";
    const mainImageUrl = this.postDetails?.listing.first_image_uri
      ? `${process.env.NEXT_PUBLIC_IMAGES_CDN}/previews/700x0/${this.postDetails?.listing.first_image_uri}.webp`
      : null;
    const neighborhoodName = this.postDetails?.listing.neighborhood
      ? this.postDetails.listing.neighborhood.label
      : "";
    const seoUrl = slugifyHelper(title);

    const url =
      "https://" +
      "__host_name_full__/__lang__" +
      "/search/" +
      this.postDetails.listing.listing_id +
      "/" +
      seoUrl;
    const mainImageStructure = mainImageUrl
      ? {
          "@type": "ImageObject",
          caption: title,
          width: 700,
          contentUrl: mainImageUrl,
        }
      : null;

    const getRESchemaOrg = () => {
      const isApartmentSubCat = [
        "ApartmentsForSale",
        "ApartmentsForRent",
      ].includes(this.subCategory?.reporting_name);

      const json = {
        "@context": "https://schema.org",
        "@type": isApartmentSubCat ? "Apartment" : "Residence",
        name: title,
        url: url,
        address: {
          "@type": "PostalAddress",
          addressCountry: labelHelper(this.country, this.lang),
          addressRegion: labelHelper(city, this.lang),
          ...(neighborhoodName ? { addressLocality: neighborhoodName } : {}),
        },
        ...(isApartmentSubCat
          ? {
              numberOfRooms:
                this.getDynamicAttribute("Rooms")?.["option_value"],
            }
          : { numberOfRooms: 0 }),
        ...(this.postDetails.listing.post_map &&
        Object.keys(this.postDetails.listing.post_map).length > 0
          ? {
              geo: {
                "@type": "GeoCoordinates",
                latitude: this.postDetails.listing.post_map.lat,
                longitude: this.postDetails.listing.post_map.lng,
              },
              hasMap: `https://www.google.com/maps?q=${this.postDetails.listing.post_map.lat},${this.postDetails.listing.post_map.lng}`,
            }
          : {}),
        ...(mainImageStructure ? { photo: mainImageStructure } : {}),
      };
      return JSON.stringify(json);
    };

    const getJobPosterSchemaOrg = () => {
      if (!description) return null;
      const json = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        url: url,
        industry: this.subCategory?.label,
        jobLocation: {
          address: {
            name: city.name,
            addressLocality: city.name,
          },
        },
        title,
        description,
        hiringOrganization: {
          name: this.postDetails.seller.full_name,
          logo: "https://opensooqui2.os-cdn.com/os_web/opensooq-job-posting-logo.png",
        },
        validThrough: this.postDetails.listing.price_valid_until,
        datePosted:  new Date(
            5454545 * 1000
        ).toISOString(),
        ...(this.getDynamicAttribute("Job_Type")
          ? {
              employmentType:
                this.getDynamicAttribute("Job_Type")?.[labelField],
            }
          : {}),
        ...(this.getDynamicAttribute("Job_Type")["option_value"] ==
        "Work from Home"
          ? { jobLocationType: "TELECOMMUTE" }
          : {}),
        ...(this.getDynamicAttribute("Education_Jobs")?.[labelField]
          ? {
              educationRequirements:
                this.getDynamicAttribute("Education_Jobs")?.[labelField],
            }
          : {}),
        ...(this.getDynamicAttribute("Experience_Jobs")?.[labelField]
          ? {
              experienceRequirements:
                this.getDynamicAttribute("Experience_Jobs")?.[labelField],
            }
          : {}),
      };
      return JSON.stringify(json);
    };

    const getServicesSchemaOrg = () => {
      const json = {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: this.subCategory?.label,
        url,
        name: title,
        description,
        offers: {
          "@type": "Offer",
          url,
          description,
          priceCurrency: "",
          price: "",
          ...(this.postDetails.listing.price.price
            ? {
                price: this.postDetails.listing.price.price,
                priceCurrency: this.country.currency_en,
                priceValidUntil: this.postDetails.listing.price_valid_until,
              }
            : {}),
        },
        ...(mainImageStructure ? { image: mainImageStructure } : {}),
      };
      return JSON.stringify(json);
    };

    const getElectronicsSchemaOrg = () => {
      const brands = [
        "TV_Brands",
        "Type_ComputersLaptops_child",
        "Type_ReceiversAndDVDPlayer_child",
        "Type_CamerasPhotography_child",
        "Type_AirConditionersAndFans_child",
        "Type_RefrigeratorsFreezers_child",
        "Type_WashingMachinesAndDryers",
        "Type_OvensMicrowaves_child",
        "Type_WearableTechnology_child",
        "Type_Heaters_child",
        "Type_Heater",
        "Type_Vacuum_Cleaner",
        "Type_Consoles",
        "Phones_Brand",
        "Phones_Brand_child",
      ];

      const categoryKeys = [
        "Type_ComputersLaptops",
        "Type_SpeakersAndAmplifiers",
        "Type_StereoAndRadios",
        "Type_ReceiversAndDVDPlayer",
        "Type_CamerasPhotography",
        "Type_AirConditionersAndFans",
        "Type_RefrigeratorsFreezers",
        "Type_WashingMachines",
        "Type_OvensMicrowaves",
        "Type_WaterCoolersAndFilters",
        "Type_WearableTechnology",
        "Type_Heaters",
        "Type_PrinterScannerFax",
        "Type_Accessories",
        "Type_Consoles",
      ];

      const conditionKeys = [
        "TV_Condition",
        "Computers_Condition",
        "Tablets_Condition",
        "Phones_Condition",
        "ConditionUsed",
        "Type_ProfessionalEquipment_age",
      ];

      const [brandSchemaVal] = brands.filter((field_name) => {
        return this.getDynamicAttribute(field_name)?.[labelField];
      });

      const [categorySchemaVal] = categoryKeys.filter((field_name) => {
        return this.getDynamicAttribute(field_name)?.[labelField];
      });

      const [conditionSchemaVal] = conditionKeys.filter((field_name) => {
        return this.getDynamicAttribute(field_name)?.[labelField];
      });
      if (!this.postDetails.listing.price) {
        return null;
      }

      const json = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: title,
        sku: this.postDetails.listing.listing_id,
        description,
        url,
        category: categorySchemaVal
          ? " / " + categorySchemaVal
          : this.category.label + "/" + this.subCategory?.label,
        ...(brandSchemaVal
          ? { brand: " / " + brandSchemaVal, category: "/" + brandSchemaVal }
          : {}),
        ...(this.getDynamicAttribute("Type_Screensize")
          ? { width: this.getDynamicAttribute("Type_Screensize")?.[labelField] }
          : {}),
        ...(mainImageStructure ? { image: mainImageStructure } : {}),
        offers: {
          "@type": "Offer",
          url,
          description,
          availability: "https://schema.org/InStock",
          ...(conditionSchemaVal
            ? {
                itemCondition: ["old", "new"].includes(
                  conditionSchemaVal.toLocaleLowerCase()
                )
                  ? "https://schema.org/UsedCondition"
                  : "https://schema.org/NewCondition",
              }
            : {}),
          price: this.postDetails.listing.price.price,
          priceCurrency:this.postDetails.listing.price.currencies[0].currency_code,
          priceValidUntil: this.postDetails.listing.price_valid_until,
        },
      };
      return JSON.stringify(json);
    };

    const getCarsForSaleSchemaOrg = () => {
      const make = this.getDynamicAttribute("Brand");
      const color = this.getDynamicAttribute("Car_Color");
      const year = this.getDynamicAttribute("Car_Year");
      const transmission = this.getDynamicAttribute("Tramsmission_Cars");
      const bodyType = this.getDynamicAttribute("Cars_body_types") || {};
      const conditionUsed = this.getDynamicAttribute("ConditionUsed");

      const json = {
        "@context": "https://schema.org",
        "@type": "Car",
        name: title,
        description,
        brand: make?.[labelField] || null,
        url,
        ...(mainImageStructure ? { image: mainImageStructure } : {}),
        offers: {
          "@type": "Offer",
          url,
          description,
          availability: "https://schema.org/InStock",
          priceCurrency: "",
          price: "",
          ...(conditionUsed
            ? {
                itemCondition:
                  conditionUsed?.option_label == "New"
                    ? "https://schema.org/NewCondition"
                    : "https://schema.org/UsedCondition",
              }
            : {}),
          ...(this.postDetails.listing.price.price
            ? {
                price: this.postDetails.listing.price.price,
                priceCurrency: this.country.currency_en,
                priceValidUntil:
                  this.postDetails.listing.price_valid_until,
              }
            : {}),
        },
        ...(color?.[labelField] ? { color: color[labelField] } : {}),
        ...(year?.[labelField] ? { productionDate: color[labelField] } : {}),
        ...(transmission?.[labelField]
          ? { vehicleTransmission: transmission[labelField] }
          : {}),
        ...(bodyType?.[labelField] ? { bodyType: bodyType[labelField] } : {}),
      };
      return JSON.stringify(json);
    };

    //todo @yasmeen we should replace posted_date with record_expiration_date_timestamp
    const getSeekersAndFunnySchemaOrg = () => {
      const json = {
        "@context": "https://schema.org",
        "@type": "Offer",
        url,
        name: title,
        description,
        serviceType: this.subCategory?.label,
        price: "",
        priceCurrency: "",
        ...(this.postDetails.listing.price
          ? {
              price: this.postDetails.listing.price,
              priceCurrency: this.country.currency_en,
              priceValidUntil:
                this.postDetails.listing.price_valid_until,
            }
          : {}),
        ...(mainImageStructure ? { image: mainImageStructure } : {}),
      };
      return JSON.stringify(json);
    };

    switch (this.category?.reporting_name) {
      case "RealEstateForSale":
      case "RealEstateForRent":
        return [schemaOrg, getRESchemaOrg()];
      case "JobPoster":
        return [schemaOrg, getJobPosterSchemaOrg()];
      case "Electronics":
        return [schemaOrg, getElectronicsSchemaOrg()];
      case "Autos":
        if (this.subCategory?.reporting_name == "CarsForSale") {
          return [schemaOrg, getCarsForSaleSchemaOrg()];
        } else return [schemaOrg, null];
      case "Services":
        return [schemaOrg, getServicesSchemaOrg()];
      default:
        if (
          ["Consoles", "MobilePhones", "Tablets"].includes(
            this.subCategory?.reporting_name
          )
        ) {
          return [schemaOrg, getElectronicsSchemaOrg()];
        }
        if (["JobSeekers", "Funny"].includes(this.subCategory?.reporting_name))
          return [schemaOrg, getSeekersAndFunnySchemaOrg()];
        return [schemaOrg, null];
    }
  }

  private setCategory(category: ICategoryAsset | ICategoryLiteAsset) {
    this.category = category;
  }

  private setSubCategory(subCategory: ICategoryAsset | ICategoryLiteAsset) {
    this.subCategory = subCategory;
  }

  private setPostDetails(postDetails: iPostListingData) {
    this.postDetails = postDetails;
  }

  private setJobDetails(postDetails: IJobPosterPostView | IJobSeekerPostView) {
    this.jobDetails = postDetails;
  }

  private seoTr(key: string): string {
    const tr = {
      postDescPrefix: {
        ar: "اعلان رقم",
        en: "Listing No",
      },
      opensooq: {
        ar: "السوق المفتوح",
        en: "OpenSooq",
      },
    };

    return tr[key][this.lang];
  }

  public getFindScreen(
    queryString: IFindPageQueryString,
    screenKey: EnumScreenNames
  ): ISeoPage {
    const seoPage: ISeoPage = {
      pageH1: this.localization.trSeoField(screenKey, "pageH1", {
        query: queryString.PostSearch?.term || "",
      }),
      pageTitle: this.localization.trSeoField(screenKey, "pageH1", {
        query: queryString.PostSearch?.term || "",
      }),
    };
    return seoPage;
  }

  public getArchivePostSeoData(postDetails) {
    if ("show_related_posts" in postDetails) {
      const { post } = postDetails;
      return {
        pageTitle: post.title
          ? `${post.title} - ${post.id} | ${this.seoTr("opensooq")}`
          : this.seoTr("opensooq"),
        canonical: post.post_url,
        pageMetaDescription: `${(post.description || "").substring(0, 150)}`,
      };
    } else if ("archive" in postDetails) {
      const { archive } = postDetails;
      return {
        pageTitle: archive.post.title,
        canonical: archive.post.post_url,
        pageMetaDescription: `${archive.message}`,
      };
    }
  }
  public getDLPScreenSeoData(postsApiResponse : IPostsApiResponse,requestUri:string): ISeoPage {
    const title = postsApiResponse.model.name || '';
    const pageH2 = postsApiResponse.model.description || '';
    const breadcrumbs: ITypeBreadcrumb[] = [
      { label: title, url: "" },
    ];
    const seoPage: ISeoPage = {
      pageTitle: title,
      pageH1: title,
      pageH2: pageH2,
      link2:requestUri.replace(/(en|ar)\//, this.lang2 + "/"),
      breadcrumbs
    };
    return seoPage;
  }

  public getPostViewScreenSeoData({
    postDetails,
  }: {
    postDetails: iPostListingData;
  }): ISeoPage {
    const category = this.essentialData.getMainCategoryByReportingName(
        postDetails?.listing?.category.reporting_name
    );
    const subCategory = this.essentialData.getSubCategoryByReportingName(
        category,
        postDetails?.listing?.sub_category.reporting_name
    );
    this.setCategory(category);
    this.setSubCategory(subCategory);
    this.setPostDetails(postDetails);
    const city = this.essentialData.getCityById(postDetails.listing?.city.id);
    const neighbourhood =
      this.essentialData.getNeighborhoodFromCitiesByNeighborhoodId(
        postDetails.listing?.neighborhood?.id
      );

    const ogImages: string[] = (postDetails.listing?.media || [])
      .filter((item) => item.uri)
      .map(
        (item) =>
          `https://opensooq-images.os-cdn.com/previews/220x0/${item.uri}.webp`
      );

    const postLang = postDetails.listing?.lang == "fr" ? "en" : postDetails.listing?.lang;

    let canonical = postDetails?.listing?.post_url?.replace(
      /(en|ar|fr)\//,
      postLang + "/"
    );
    const getpageTitle = () => {};
    const seoPage: ISeoPage = {
      pageTitle: this.getPageTitle(),
      pageH1: postDetails.listing.title,
      pageMetaDescription: `${this.seoTr("postDescPrefix")} ${
        postDetails.listing.listing_id
      }  ${(postDetails.listing.masked_description|| "").slice(0, 130)}`,
      canonical,
      link2: postDetails.listing.post_url.replace(/(en|ar)\//, this.lang2 + "/"),
      ogImages,
      robots: getCustomIndexStrategy(
        EnumScreenNames.POST_VIEW_SCREEN,
        {
          categoryReportingName: category?.reporting_name,
        },
        this.lang
      ),
      "schema.org": this.getSchemaOrg(),
      breadcrumbs: generateBreadcrumbs(
        [
          city,
          isCategoryRealEstate(category) ? neighbourhood : null,
          category,
          subCategory,
          ...postDetails.listing.basic_info.items.filter(
            (item) => item.is_indexable
          ),
        ].filter(Boolean),
        this.lang
      ),
    };
//todo yasmeen replace value with indexabel id
    return seoPage;
  }

  public getTopSoldCars = (screenKey: EnumScreenNames, allTopSoldCars: any): ISeoPage => {
    if (!allTopSoldCars) return null;

    const data: ISeoPage = this.localization.getJson(screenKey);
    const dataCopy2 = JSON.parse(JSON.stringify(data));
    const faqs = dataCopy2?.FQA;

    if (!faqs || faqs.length === 0) return { ...dataCopy2 };

    let fqaAnswer = faqs[0].answer;

    for (let r = 1; r <= 5; r++) {
      const makeKey = `__make${r}__`;
      const modelKey = `__model${r}__`;
      const car = allTopSoldCars?.items[r-1];

      if (car) {
        fqaAnswer = fqaAnswer.replace(makeKey, car.label);
        fqaAnswer = fqaAnswer.replace(modelKey, car.brands[0]?.label || '');
      }
    }


    dataCopy2.FQA[0].answer = fqaAnswer;
    return { ...dataCopy2 };
  };

  public getJobViewScreenSeoData({
    postDetails,
  }: {
    postDetails: IJobSeekerPostView | IJobPosterPostView;
  }): ISeoPage {
    this.setJobDetails(postDetails);

    let canonical = postDetails.extraData.seoData.postUrl;
    const city = this.essentialData.getCityById(postDetails.city_id);
    const category = this.essentialData.getMainCategoryByReportingName(
      this.jobDetails.category_reporting_name
    );
    const subCategory = this.essentialData.getSubCategoryByReportingName(
      category,
      this.jobDetails.sub_category_reporting_name
    );
    this.setCategory(category);
    this.setSubCategory(subCategory);
    const isPoster = (
      arg: IJobPosterPostView | IJobSeekerPostView
    ): arg is IJobPosterPostView => {
      return "dynamicAttributesObject" in arg;
    };

    let pageMetaDescription = `${this.seoTr("postDescPrefix")} ${
        postDetails.extraData.seoData.postId
    }  ${(postDetails.extraData.seoData.description || "").slice(0, 130)}`;


    let pageTitle  =  postDetails.extraData.seoData.pageTitle;
    let breadcrumbs = [];

  if (!  isPoster(postDetails)) { //this customzation just for CV
    let post_hash =  postDetails.card.id.split('-').slice(-1)[0]
    let city = labelHelper(this.essentialData.getCityById(postDetails.city_id), this.lang);
    pageTitle = this.localization.trPlaceholder(EnumScreenNames.JOB_VIEW_SCREEN, "pageTitle", {
      memberName: postDetails.card.name,
      Sector_JobSeeker: postDetails.card.sector,
      post_hash,
      city,
    });

    pageMetaDescription = this.localization.trPlaceholder(EnumScreenNames.JOB_VIEW_SCREEN, "pageMetaDescription", {
      memberName: postDetails.card.name,
      Sector_JobSeeker: postDetails.card.sector,
      post_hash,
      city,
    });
    breadcrumbs = generateBreadcrumbs(
        [
          city,
          category,
          subCategory,
          {'label' : postDetails.role, 'url_name' :postDetails.roleOption ? urlHelper(postDetails.roleOption ,this.lang) : null}
        ].filter(Boolean),
        this.lang
    );

  } else{
    breadcrumbs = generateBreadcrumbs(
        [
          city,
          category,
          subCategory,
          ...postDetails.dynamicAttributesObject.filter(
              (item) => item.is_indexable
          ),
        ].filter(Boolean),
        this.lang
    );

    breadcrumbs.push({
      label: postDetails.card.title,
      url: canonical,
    });
  }
  breadcrumbs = JSON.parse(JSON.stringify(breadcrumbs).replaceAll("//","/"))
  let seoPage: ISeoPage = {
    pageTitle ,
    pageMetaDescription,
      pageH1: postDetails.extraData.seoData.pageTitle,
     canonical,
      link2: postDetails.extraData.seoData.link2,
      breadcrumbs
    };

    seoPage = {
      ...seoPage,
      "schema.org": [generateBreadcrumbsSchemaOrg(breadcrumbs)],
      robots: getCustomIndexStrategy(
        EnumScreenNames.POST_VIEW_SCREEN,
        {
          categoryReportingName: subCategory?.reporting_name,
        },
        this.lang
      ),
    };
    return seoPage;
  }

  private getJobPosterSchemaOrg = (jobDetails: IJobPosterPostView, city) => {
    const getDynamicAttribute = (field_name: string) => {
      const dynamicAttributes: { [key: string]: IDynamicAttributesObject } = {};
      for (let item of jobDetails.dynamicAttributesObject) {
        dynamicAttributes[item.field_name] = item;
      }
      this.jobsDynamicAttributes = dynamicAttributes;
      return this.dynamicAttributes[field_name];
    };

    const title = jobDetails.extraData.seoData.description;
    const description = jobDetails?.extraData.seoData.description.slice(0, 300);
    const labelField = this.lang == "ar" ? "label" : "label_en";
    const mainImageUrl = jobDetails?.card.company_profile_picture
      ? `${process.env.NEXT_PUBLIC_IMAGES_CDN}/previews/700x0/${jobDetails?.card.company_profile_picture}.webp`
      : null;
    const neighborhoodName = jobDetails.neighborhoodName;
    const seoUrl = slugifyHelper(title);

    const url =
      "https://" +
      "__host_name_full__/__lang__" +
      "/search/" +
      jobDetails.card.post_id +
      "/" +
      seoUrl;
    const mainImageStructure = mainImageUrl;
    if (!description) return null;
    const json = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      url: url,
      industry: this.subCategory?.label,
      jobLocation: {
        address: {
          name: city.name,
          addressLocality: city.name,
        },
      },
      title,
      description,
      hiringOrganization: {
        name: jobDetails.card.company_name,
        logo: "https://opensooqui2.os-cdn.com/os_web/opensooq-job-posting-logo.png",
      },
      validThrough: "",
      datePosted: jobDetails.card.created_date_timestamp,
      ...(getDynamicAttribute("Job_Type")
        ? {
            employmentType: getDynamicAttribute("Job_Type")?.[labelField],
          }
        : {}),
      ...(getDynamicAttribute("Job_Type")?.["option_value"] == "Work from Home"
        ? { jobLocationType: "TELECOMMUTE" }
        : {}),
      ...(getDynamicAttribute("Education_Jobs")?.[labelField]
        ? {
            educationRequirements:
              this.getDynamicAttribute("Education_Jobs")?.[labelField],
          }
        : {}),
      ...(this.getDynamicAttribute("Experience_Jobs")?.[labelField]
        ? {
            experienceRequirements:
              getDynamicAttribute("Experience_Jobs")?.[labelField],
          }
        : {}),
    };
    return [JSON.stringify(json)];
  };
  private getJobSeekersSchemaOrg = (jobDetails: IJobSeekerPostView) => {
    const title = jobDetails.extraData.seoData.description;
    const description = jobDetails?.extraData.seoData.description.slice(0, 300);
    const mainImageUrl = jobDetails?.card.avatar
      ? `${process.env.NEXT_PUBLIC_IMAGES_CDN}/previews/700x0/${jobDetails?.card.avatar}.webp`
      : null;
    const seoUrl = slugifyHelper(title);

    const url =
      "https://" +
      "__host_name_full__/__lang__" +
      "/search/" +
      jobDetails.card.post_id +
      "/" +
      seoUrl;
    const mainImageStructure = mainImageUrl;
    if (!description) return null;
    const json = {
      "@context": "https://schema.org",
      "@type": "Offer",
      url,
      name: title,
      description,
      serviceType: this.subCategory?.label,
      price: "",
      // priceCurrency: "",
      // ...(this.postDetails.price
      //     ? {
      //       price: this.postDetails.price,
      //       priceCurrency: this.country.currency_en,
      //       priceValidUntil:
      //       this.postDetails.record_expiration_date_timestamp,
      //     }
      //     : {}),
      ...(mainImageStructure ? { image: mainImageStructure } : {}),
    };
    return [JSON.stringify(json)];
  };

  private getPageTitle(): string {
    if (this.postDetails.listing?.title.length > 20) {
      let brand = this.lang=="en"? "OpenSooq" : "السوق المفتوح";

      if (isShobiddak()) {
        brand = this.lang == "en" ? "ShoBiddak" : "شو بدك";
      }

      let title = this.postDetails.listing.title + " - ";
      title +=
        "(" + this.postDetails.listing.listing_id + ")  | " + brand;
      return title;
    }
    return this.getTitleTemplate();
  }

  private getTitleTemplate(): string {
    let placeholders = this.getTitlePlaceHolders();
    let placeholdersKeys = Object.keys(placeholders);
    let placeholdersVals = Object.values(placeholders);
    let template =
      "__post_title__ : __sub_category__ __param1__ __param2__ : __market_name__ __neighborhood__ (__post_id__)";
    placeholdersKeys.forEach((key, index) => {
      template = template.replace(key, placeholdersVals[index]);
    });
    return template;
  }

  private getTitlePlaceHolders(): ITitlePlaceholders {
    const [cp1, cp2, cp3]: iBasicInfoItem[] = [null, null ,null]
    // const [cp1, cp2, cp3]: iBasicInfoItem[] =
    //   this.prepareDynamicAttributeObject();
    return {
      __country__: labelHelper(this.country, this.lang),
      __post_title__: this.postDetails?.listing?.title,
      __sub_category__: labelHelper(this.subCategory, this.lang),
      __param1__: cp1?.field_label || "",
      __param2__: cp2?.field_label || "",
      __param3__: cp3?.field_label || "",
      __neighborhood__: labelHelper(this.postDetails.listing?.neighborhood, this.lang),
      __market_name__: this.postDetails.listing?.city.label,
      __post_id__: Number(this.postDetails.listing.listing_id),
      __published_date__: this.postDetails.listing?.publish_date,
    };
  }

  private prepareDynamicAttributeObject() {
    return this.postDetails.listing?.basic_info.items.filter((dynAtt) => {
      return dynAtt.option_label != "boolean";
    });
  }

  getMemberScreenSeoData(
    screenKey: EnumScreenNames,
    memberData: IMembershipInfoResponse,
    postsApiResponse: IPostsApiResponse,
    extendedQueryMid: string
  ): ISeoPage {
    const memberLink = memberData?.member?.branding?.share?.link;
    const data: ISeoPage = {
      pageTitle: this.localization.trSeoField(screenKey, "pageTitle", {
        memberName: memberData?.member?.branding?.name,
        memberId: memberData?.member?.id.toString(),
      }),
      pageMetaDescription: this.localization.trSeoField(
        screenKey,
        "pageMetaDescription",
        {
          adsCount: postsApiResponse._meta.totalCount,
          memberName: memberData?.member?.branding?.name,
        }
      ),
      canonical: `${memberData?.member?.branding?.share?.link}`,
      link2: memberLink.includes("/ar/")
        ? memberLink.replace("/ar/", "/en/")
        : memberLink.replace("/en/", "/ar/") +
          (extendedQueryMid ? `?info=${extendedQueryMid}` : ""),
      robots: "noindex,nofollow",
      breadcrumbs: this.generateMemberBreadcrumbs(
        this.country,
        memberData,
        this.localization
      ),
    };
    return data;
  }

  getShopListingIndex(screenKey: EnumScreenNames): ISeoPage {
    const data: ISeoPage = this.localization.getJson(screenKey);
    return {
      ...data,
      link2: data.link2,
      canonical: data.canonical,
    };
  }

  getBanksDetailsScreenSeoData(
    screenKey: EnumScreenNames,
    bankDetails: IBankDetails
  ): ISeoPage {
    let data: ISeoPage = this.localization.getJson(screenKey);
    const breadcrumbs: ITypeBreadcrumb[] = [
      ...data.breadcrumbs,
      { label: bankDetails.label, url: bankDetails.url },
    ];
    data = {
      ...data,
      pageTitle: bankDetails.title,
      pageMetaDescription: bankDetails.meta,
      canonical: bankDetails.url,
      link2: this.localization.trSeoField(screenKey, "link2", {
        url_name: bankDetails[`url_${this.lang2}`],
      }),
      breadcrumbs,
      "schema.org": [
        generateBreadcrumbsSchemaOrg(breadcrumbs),
        JSON.stringify(bankDetails.faqData),
      ],
    };

    return data;
  }

  getTagsMainScreenSeoData(
    screenKey: EnumScreenNames,
    _meta: IMetaData
  ): ISeoPage {
    let data: ISeoPage = this.localization.getJson(screenKey);

    const breadcrumbs = generateBreadcrumbs(
      [
        { ...this.country, url_name_ar: "ar", url_name_en: "en" },
        {
          label_en: "Tags",
          label_ar: "كلمات وصفية",
          url_name_en: "tags",
          url_name_ar: "tags"
        },
      ],
      this.lang
    );
    data = {
      ...data,
      canonical:
        data.canonical +
        (_meta.currentPage > 1 ? `?page=${_meta.currentPage}` : ""),
      link2:
        data.link2 +
        (_meta.currentPage > 1 ? `?page=${_meta.currentPage}` : ""),
      pageTitle: this.localization.trSeoField(screenKey, "pageTitle", {
        page: _meta.currentPage,
        totalPage: _meta.totalCount,
      }),
      pageH1: this.localization.trSeoField(screenKey, "pageH1", {
        page: _meta.currentPage,
        totalPage: _meta.totalCount,
      }),
      pageMetaDescription: this.localization.trSeoField(
        screenKey,
        "pageMetaDescription",
        {
          page: _meta.currentPage,
          totalPage: _meta.totalCount,
        }
      ),
      breadcrumbs,
      "schema.org": [generateBreadcrumbsSchemaOrg(breadcrumbs)]
    };

    return data;
  }

  getTagsDetailsScreenSeoData(
    screenKey: EnumScreenNames,
    postsByTagsApiResponse: IPostsByTagsApiResponse,
    essentialData: EssentialData
  ): ISeoPage {
    const category = essentialData.getMainCategoryById(
      postsByTagsApiResponse.model.category_id
    );
    const breadcrumbs = this.generateTagsDetailsBreadcrumbs(
      [
        { ...this.country, url_name_ar: "ar", url_name_en: "en" },
        {
          label_en: "Tags",
          label_ar: "كلمات وصفية",
          url_name_en: "tags",
          url_name_ar: "tags"
        },
        (category && {
          label_en: category.label_en,
          label_ar: category.label_ar,
          url_name_en: "category/" + category.url_name_en,
          url_name_ar: "category/" + category.url_name_ar
        }),
        {
          label_ar: postsByTagsApiResponse.model.keyword,
          label_en: postsByTagsApiResponse.model.keyword,
          url_name_ar: slugifyHelper(postsByTagsApiResponse.model.keyword),
          url_name_en: slugifyHelper(postsByTagsApiResponse.model.keyword)
        }
      ],
      this.lang,
      category
    );

    // ,url_name_ar:urlHelper(this.country,this.lang).replace("/",'')
    const data: ISeoPage = {
      canonical: postsByTagsApiResponse.seo["canonical"],
      pageTitle: postsByTagsApiResponse.seo["title"],
      pageH1: postsByTagsApiResponse.model.keyword,
      pageArticle: postsByTagsApiResponse.model.page_description,
      link2: postsByTagsApiResponse.seo["canonical"].replace(
        /\/(ar|en)/,
        `/${this.lang2}`
      ),
      pageMetaDescription: postsByTagsApiResponse.seo["meta_description"],
      breadcrumbs,
      "schema.org": [generateBreadcrumbsSchemaOrg(breadcrumbs)],
      robots: getCustomIndexStrategy(screenKey, {
        tagLang: postsByTagsApiResponse.model.lang,
        currentLang: this.lang,
      }),
    };
    return data;
  }

  getSupportFaqSeoDetails(
    screenKey: EnumScreenNames,
    supportFaqUrlName: string,
    faqCategoriesAr: IFaqCategory[],
    faqCategoriesEn: IFaqCategory[]
  ): ISeoPage {
    const currentFaqCategory: IFaqCategory = (
      this.lang === "ar" ? faqCategoriesAr : faqCategoriesEn
    ).filter((c) => c.url_name === supportFaqUrlName)[0];
    const currentFaqCategory2: IFaqCategory = (
      this.lang === "ar" ? faqCategoriesEn : faqCategoriesAr
    ).filter((c) => c.id === currentFaqCategory.id)[0];
    const pageName = currentFaqCategory.name;
    const url_name2 = currentFaqCategory2.url_name;

    const data: ISeoPage = {
      pageTitle: this.localization.trSeoField(screenKey, "pageTitle", {
        pageName,
      }),
      link2: this.localization.trSeoField(screenKey, "link2", {
        url_name: url_name2,
      }),
      pageMetaDescription: this.localization.trSeoField(
        screenKey,
        "pageMetaDescription"
      ),
    };
    return data;
  }

  getTagsCategoriesScreenSeoData(
    screenKey: EnumScreenNames,
    _meta: IMetaData,
    category: ICategoryAsset
  ): ISeoPage {
    let data: ISeoPage = this.localization.getJson(screenKey);

    const breadcrumbs = generateBreadcrumbs(
      [
        { ...this.country, url_name_ar: "ar", url_name_en: "en" },
        {
          label_en: "Tags",
          label_ar: "كلمات وصفية",
          url_name_en: "tags",
          url_name_ar: "tags"
        },
        (category && {
          label_en: category.label_en,
          label_ar: category.label_ar,
          url_name_en: "category/" + category.url_name_en,
          url_name_ar: "category/" + category.url_name_ar
        })
      ],
      this.lang
    );
    const category_cleaned = category || {
      url_name_ar: "اخرى",
      url_name_en: "others",
    };

    let link2 = this.localization.trSeoField(screenKey, "link2", {
      categoryUrlLang2: urlHelper(
        category_cleaned,
        this.lang == "ar" ? "en" : "ar"
      ),
    });

    data = {
      ...data,
      pageTitle: this.localization.trSeoField(screenKey, "pageTitle", {
        page: _meta.currentPage,
        categoryName: labelHelper(category, this.lang),
      }),
      pageMetaDescription: this.localization.trSeoField(
        screenKey,
        "pageMetaDescription",
        {
          page: _meta.currentPage,
          categoryName: labelHelper(category, this.lang),
        }
      ),
      canonical:
        this.localization.trSeoField(screenKey, "canonical", {
          categoryUrl: urlHelper(category, this.lang),
        }) + (_meta.currentPage > 1 ? `?page=${_meta.currentPage}` : ""),
      link2:
        link2 + (_meta.currentPage > 1 ? `?page=${_meta.currentPage}` : ""),
      breadcrumbs: breadcrumbs,
      "schema.org":[
        generateBreadcrumbsSchemaOrg(breadcrumbs)
      ]
    };
    return data;
  }

  getWeatherSeoDataByCity(
    screenKey: EnumScreenNames,
    city: ICityAsset
  ): ISeoPage {
    let data: ISeoPage = this.localization.getJson(screenKey);
    let breadcrumbs = generateBreadcrumbs(
      [
        {
          label_en: `Weather in ${labelHelper(
            this.country,
            "en"
          )} ${labelHelper(city, "en")} : Temperatures : OpenSooq : Updated`,
          url_name_en: `weather/${urlHelper(city, "en")}`,
          label_ar: `حالة الطقس على السوق المفتوح : ${labelHelper(
            city,
            "ar"
          )} ${this.country.name_arabic} : درجات الحرارة : تحديث مستمر`,
          url_name_ar: `حالة-الطقس/${urlHelper(city, "ar")}`,
        },
      ],
      this.lang
    );
    let canonical = breadcrumbs[breadcrumbs.length - 1]?.url || null;
    data = {
      ...data,
      pageTitle: this.localization.trSeoField(screenKey, "pageTitle", {
        cityName: labelHelper(city, this.lang),
        cityUrl: urlHelper(city, this.lang),
      }),

      link2: this.localization.trSeoField(screenKey, "link2", {
        cityUrl: urlHelper(city, this.lang == "ar" ? "en" : "ar"),
      }),
      pageMetaDescription: this.localization.trSeoField(
        screenKey,
        "pageMetaDescription",
        {
          cityName: labelHelper(city, this.lang),
        }
      ),
      hrefLangs: generateHrefLangs(
        {
          urlAr: `حالة-الطقس/${urlHelper(city, "ar")}`,
          urlEn: `weather/${urlHelper(city, "en")}`,
        },
        this.lang,
        this.country,
        "opensooq.com"
      ),
      breadcrumbs,
      canonical,
    };
    return data;
  }

  getPrayerSeoDataByCity(
    screenKey: EnumScreenNames,
    city: ICityAsset
  ): ISeoPage {
    let data: ISeoPage = this.localization.getJson(screenKey);

    const breadcrumbs = generateBreadcrumbs(
      [
        {
          label_en: "Prayer Times",
          url_name_en: `prayer-times/${urlHelper(city, "en")}`,
          label_ar: "أوقات الصلاة",
          url_name_ar: `أوقات-الصلاة/${urlHelper(city, "ar")}`,
        },
      ],
      this.lang
    );

    const canonical = breadcrumbs[breadcrumbs.length - 1].url;

    data = {
      ...data,
      pageTitle: this.localization.trSeoField(screenKey, "pageTitle", {
        cityName: labelHelper(city, this.lang),
        cityUrl: urlHelper(city, this.lang),
      }),
      link2: this.localization.trSeoField(screenKey, "link2", {
        cityUrl: urlHelper(city, this.lang == "ar" ? "en" : "ar"),
      }),
      hrefLangs: generateHrefLangs(
        {
          urlAr: `أوقات-الصلاة/${urlHelper(city, "ar")}`,
          urlEn: `prayer-times/${urlHelper(city, "en")}`,
        },
        this.lang,
        this.country,
        "opensooq.com"
      ),
      breadcrumbs,
      canonical,
    };
    return data;
  }
  public getLandingBrandCategories = (
      screenKey,
      categories:IMainCategories[]
  ) => {
    let data: ISeoPage = this.localization.getJson(screenKey);
    data = {
      ...data,
    };
    data = {
        ...data,
        "schema.org": data.breadcrumbs && data.breadcrumbs.length > 0 ? [generateBreadcrumbsSchemaOrg(data.breadcrumbs)] : [],
    }

    return data;
  }
  public getLandingBrands = (
      screenKey,
      category,
      brands,
  ) => {
    if (!brands) return null;
    let data: ISeoPage = this.localization.getJson(screenKey);
    const _meta = brands && brands?._meta;

    const hrefLangs = data.hrefLangs.map((item) => {
      let href = item.href;

      if (this.lang === 'ar') {
        href = href.replace('__ar_main_cat__', category?.url);
      } else if (this.lang === 'en') {
        href = href.replace('__en_main_cat__', category?.url);
      }

      return {
        href: href,
        hreflang: item.hreflang
      };
    });
    let breadcrumbs = data.breadcrumbs?.map((item) => {
      let label = item.label;
      let url = item.url;
        if (this.lang === 'ar') {
            label = label.replace('__ar_main_cat__', category?.name_ar);
            url = url.replace('__ar_main_cat__', category?.url);
        } else if (this.lang === 'en') {
            label = label.replace('__en_main_cat__', category?.name_en);
            url = url.replace('__en_main_cat__', category?.url);
        }
        return {
            label: label,
            url: url
        }
    })

    data = {
      ...data,
      canonical:
          `/${this.lang}/brands/${category?.url}` +
          (_meta.currentPage > 1 ? `?page=${_meta.currentPage}` : ""),
      link2: `/${this.lang2}/brands/${category?.url}`,
      pageTitle: data.pageTitle.replace(this.lang === 'ar' ? '__ar_main_cat__' : '__en_main_cat__'
          , this.lang === 'ar' ? category.name_ar : category.name_en),
      pageH1: data.pageH1.replace(this.lang === 'ar' ? '__ar_main_cat__' : '__en_main_cat__'
          , this.lang === 'ar' ? category.name_ar : category.name_en),
      pageMetaDescription: data.pageMetaDescription.replace(this.lang === 'ar' ? '__ar_main_cat__' : '__en_main_cat__'
          , this.lang === 'ar' ? category.name_ar : category.name_en),
      hrefLangs: hrefLangs,
      breadcrumbs: breadcrumbs || null,
      "schema.org": breadcrumbs && breadcrumbs.length > 0 ? [generateBreadcrumbsSchemaOrg(breadcrumbs)] : []
    };
    return data;
  }

  public getCategoriesForBrand = (
      screenKey,
      category,
      brands,
      categoriesForBrand
  ) => {
    if (!categoriesForBrand) return null;
    let data: ISeoPage = this.localization.getJson(screenKey);
    const hrefLangs = data.hrefLangs.map((item) => {
      let href = item.href;
      if (this.lang === 'ar') {
        href = href.replace('__en_brand__', categoriesForBrand?.brand?.url);
      } else if (this.lang === 'en') {
        href = href.replace('__en_brand__', categoriesForBrand?.brand?.url);
      }
      return {
        href: href,
        hreflang: item.hreflang
      };
    });
    let breadcrumbs = data.breadcrumbs?.map((item) => {
      let label = item.label;
      let url = item.url;
    if (this.lang === 'ar') {
      label = label.replace('__ar_brand__', categoriesForBrand?.brand?.label);
    } else if (this.lang === 'en') {
        label = label.replace('__en_brand__', categoriesForBrand?.brand?.label_en);
    }
        url = url.replace('__en_brand__', categoriesForBrand?.brand.url);
      return {
        label: label,
        url: url
      }
    })
    data = {
      ...data,
      canonical:
          `/${this.lang}/brands/${categoriesForBrand?.brand?.url}`,
      link2: `/${this.lang2}/brands/${categoriesForBrand?.brand?.url}`,
      pageTitle: data.pageTitle.replace( '( __en_brand__ )' ,`(${categoriesForBrand?.brand.label_en})`)
          .replaceAll(this.lang === 'ar' ? '__ar_brand__' : '__en_brand__'
          , this.lang === 'ar' ? categoriesForBrand?.brand?.label : categoriesForBrand?.brand.label_en),


      pageH1: data.pageH1.replace(this.lang === 'ar' ? '__ar_brand__' : '__en_brand__'
          , this.lang === 'ar' ? categoriesForBrand?.brand.label : categoriesForBrand?.brand.label_en),
      pageMetaDescription: data.pageMetaDescription.replace(this.lang === 'ar' ? '__ar_brand__' : '__en_brand__'
          , this.lang === 'ar' ? categoriesForBrand?.brand.label :categoriesForBrand?.brand?.label_en),
      hrefLangs: hrefLangs,
      breadcrumbs: breadcrumbs || null,
      "schema.org": breadcrumbs && breadcrumbs.length > 0 ? [generateBreadcrumbsSchemaOrg(breadcrumbs)] : []
    };
    return data;
  }

  private generateShopDetailBreadcrumbs = (
    country: ICountryAsset,
    category: ICategoryAsset,
    shopInfo: IMembershipInfoResponse,
    localization: Localization,
  ): ITypeBreadcrumb[] => {
    const breadcrumbs: ITypeBreadcrumb[] = [];

    breadcrumbs.push({
      label: labelHelper(country, localization.lang),
      url: `/${localization.lang}`,
      url2: `/${localization.lang2}`,
    });
    const linkAr = `/ar/متاجر`;
    const linkEn = `/en/shops`;
    breadcrumbs.push({
      label: localization.trPlaceholder(
        EnumScreenNames.SHOPS_LIST_SCREEN,
        "shops"
      ),
      url: localization.lang == "ar" ? linkAr : linkEn,
      url2: localization.lang != "ar" ? linkAr : linkEn,
    });
    if (category) {
      breadcrumbs.push({
        label: labelHelper(category, localization.lang),
        url:
          breadcrumbs[breadcrumbs.length - 1].url +
          "/" +
          urlHelper(category, localization.lang),
        url2:
          breadcrumbs[breadcrumbs.length - 1].url2 +
          "/" +
          urlHelper(category, localization.lang2),
      });
    }

    breadcrumbs.push({
      label:shopInfo.member?.branding?.name,
      url:shopInfo.member?.branding?.share?.link??''
    })

    return breadcrumbs;
  };

  private generateMemberBreadcrumbs = (
    country: ICountryAsset,
    memberInfo: IMembershipInfoResponse,
    localization: Localization,
  ): ITypeBreadcrumb[] => {
    const breadcrumbs: ITypeBreadcrumb[] = [];

    breadcrumbs.push({
      label: labelHelper(country, localization.lang),
      url: `/${localization.lang}`,
      url2: `/${localization.lang2}`,
    });
    const relativeMidLink =
        "/" +
        memberInfo.member?.branding?.share?.link
            ?.split("/")
            ?.slice(-3)
            ?.join("/");
    breadcrumbs.push({
      label: memberInfo.member?.branding?.name,
      url: relativeMidLink ?? "",
    });

    return breadcrumbs;
  };
  private generateTagsDetailsBreadcrumbs = <T extends object>(
    breadcrumbsArr: T[],
    lang: string,
    category?: ICategoryAsset | ICategoryLiteAsset
  ) => {
    const breadcrumbsArrCopy = breadcrumbsArr.filter(
      (item) => labelHelper(item, lang) || urlHelper(item, lang)
    );
    const breadcrumbs = [];
    const prefix = ["en", "ar"].includes(
      urlHelper(breadcrumbsArr[0], lang)
    )
      ? "/"
      : `/${lang}/`;
    breadcrumbsArrCopy.forEach((value, index) => {
      if (
        typeof value !== "object" ||
        value == null ||
        !Object.keys(value).length
      )
        return;
      let breadcrumbItem: { label: string; url?: string } = {
        label: labelHelper(value, lang)
      };

      let url = breadcrumbsArrCopy
        .slice(0, index + 1)
        .map((val) => urlHelper(val, lang))
        .join("/");
      if (category && index == breadcrumbsArr.length - 1) {
        url = url.replace(`/category/${category.url_name_ar}`, "")
          .replace(`/category/${category.url_name_en}`, "");
      }
      breadcrumbItem = {
        ...breadcrumbItem,
        url:
          prefix + url
      };

      breadcrumbs.push(breadcrumbItem);
    });
    return breadcrumbs;
  };
}
